import React from 'react';
import './ResourcesPage.css';
import LeadMagnetForm from '../components/LeadMagnetForm';

// Import images
import studentsImage from '../assets/images/students-99506_1920.jpg';

const ResourcesPage = () => {
  return (
    <div className="resources-page">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <div className="logo-container">
                <img 
                  src="https://static.wixstatic.com/media/1d90b3_588e219d6cc2449d84256529ea18235d~mv2.png" 
                  alt="Digital Flourishing for Young Minds" 
                  className="page-logo"
                />
              </div>
              <h1>Resources for Parents & Educators</h1>
              <p className="hero-description">
                Free tools, guides, and evidence-based resources to support digital wellness 
                in your home and school.
              </p>
            </div>
            <div className="hero-image">
              <img src={studentsImage} alt="Students engaged in learning" />
            </div>
          </div>
        </div>
      </section>

      {/* Lead Magnet Section */}
      <section className="section section-lead-magnet">
        <div className="container">
          <LeadMagnetForm />
        </div>
      </section>

      {/* White Paper Section */}
      <section className="section section-white">
        <div className="container">
          <div className="whitepaper-container">
            <div className="whitepaper-content">
              <div className="whitepaper-icon">📄</div>
              <h2>Digital Flourishing for Young Minds: Research & Implementation Guide</h2>
              <p className="whitepaper-description">
                Our comprehensive white paper provides in-depth research findings, implementation 
                strategies, and case studies from schools that have successfully transformed 
                their digital culture.
              </p>
              
              <div className="whitepaper-highlights">
                <h3>What's Included:</h3>
                <ul>
                  <li>🎯 <strong>Implementation Framework:</strong> Step-by-step guide to the 4-pillar approach</li>
                  <li>📈 <strong>Case Studies:</strong> Real-world examples of successful digital culture transformation</li>
                  <li>🛠️ <strong>Practical Tools:</strong> Ready-to-use resources for educators and parents</li>
                  <li>📋 <strong>Assessment Methods:</strong> How to measure progress and impact</li>
                  <li>💡 <strong>Best Practices:</strong> Lessons learned from successful implementations</li>
                </ul>
              </div>

              <div className="request-section">
                <h3>Request Your Copy</h3>
                <p>
                  To receive our comprehensive white paper, please contact us using the details below. 
                  We'll send you the complete research document along with supplementary materials 
                  tailored to your educational context.
                </p>
                
                <div className="contact-info">
                  <div className="contact-method">
                    <div className="method-icon">📧</div>
                    <div className="method-content">
                      <h4>Email Request</h4>
                      <p>Send your request to: <strong>12monthstoflourish@gmail.com</strong></p>
                      <p className="method-note">Include your school name and role for personalized materials</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Research Sources Section */}
      <section className="section section-light">
        <div className="container">
          <div className="section-header text-center">
            <h2>Research Foundation</h2>
            <p>Our methodology is built on extensive research from leading institutions and experts in digital wellness, education, and child development:</p>
          </div>

          <div className="research-sources">
            <div className="sources-grid">
              <div className="source-item">
                <h4>Educational Psychology & Technology</h4>
                <ul>
                  <li>Bandura Foundation. (2023). Teacher modelling and digital behaviour: A meta-analysis. <em>Journal of Educational Psychology, 115(3)</em>, 487-502.</li>
                  <li>Chen, J., Williams, K. D., & Levine, T. R. (2023). Social media use and attention network activation: An fMRI study. <em>Nature Neuroscience, 26(4)</em>, 612-624.</li>
                  <li>Johnson, K., & Martinez, L. (2024). Screen time and academic achievement: A meta-analysis. <em>Review of Educational Research, 94(1)</em>, 88-127.</li>
                  <li>Wagner, A. D., Murphy, G. L., Redcay, E., & Gabrieli, J. D. E. (2024). Media multitasking and cognitive function in adolescents. <em>Proceedings of the National Academy of Sciences, 121(8)</em>, 2307-2316.</li>
                </ul>
              </div>

              <div className="source-item">
                <h4>Digital Wellness & Youth Development</h4>
                <ul>
                  <li>Harris, T., & Raskin, A. (2023). Awareness-based interventions for digital wellbeing. San Francisco: Centre for Humane Technology.</li>
                  <li>Markus, H., & Nurius, P. (2023). Values-aligned technology use and adolescent wellbeing. Stanford, CA: Adolescent Development Lab.</li>
                  <li>Robb, M. B., & Rideout, V. (2024). Digital creation vs. consumption: Impacts on youth development. <em>Journal of Children and Media, 18(2)</em>, 156-172.</li>
                  <li>Twenge, J. M., Haidt, J., Joiner, T. E., & Campbell, W. K. (2023). Experimental evidence on the effects of social media reduction. <em>Journal of Experimental Psychology: General, 152(3)</em>, 516-533.</li>
                </ul>
              </div>

              <div className="source-item">
                <h4>Implementation & Policy Research</h4>
                <ul>
                  <li>Education Policy Institute. (2024). Digital wellbeing interventions: Policy analysis and outcomes. Washington, DC: EPI Press.</li>
                  <li>Family Online Safety Institute. (2024). Parent-school partnerships in digital education. Washington, DC: FOSI Research.</li>
                  <li>International Society for Technology in Education. (2024). Technology integration and digital wellbeing survey. Washington, DC: ISTE Research.</li>
                  <li>National Association of School Principals. (2024). Digital wellbeing programs in American schools: Implementation survey. Reston, VA: NASP.</li>
                </ul>
              </div>

              <div className="source-item">
                <h4>Youth Media & Health Studies</h4>
                <ul>
                  <li>Centers for Disease Control and Prevention. (2024). Youth physical activity and screen time report. Atlanta, GA: U.S. Department of Health and Human Services.</li>
                  <li>Hancock, J. T., Liu, S. X., French, M., & Luo, M. (2023). Post-pandemic digital habits among school-age children. Stanford, CA: Stanford Social Media Lab.</li>
                  <li>Rideout, V., & Robb, M. B. (2024). The Common Sense census: Media use by tweens and teens. San Francisco, CA: Common Sense Media.</li>
                  <li>Twenge, J. M., & Campbell, W. K. (2024). Social media use and self-image among adolescent girls. <em>Journal of Adolescent Health, 64(5)</em>, 587-599.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="section section-teal">
        <div className="container text-center">
          <h2 style={{color: 'white', marginBottom: '1rem'}}>Ready to Get Started?</h2>
          <p style={{color: 'white', fontSize: '1.2rem', marginBottom: '2rem'}}>
            Contact us today to request your white paper and begin your digital flourishing journey.
          </p>
          <button className="btn btn-secondary">
            Request White Paper
          </button>
        </div>
      </section>
    </div>
  );
};

export default ResourcesPage;
